/**
 * 타이틀 씬. 캐릭터 슬롯 선택·생성, 첫 입력에서 오디오를 연다.
 */

import Phaser from 'phaser';
import { bgm } from '../audio/Bgm';
import { Sfx } from '../audio/Sfx';
import { synth } from '../audio/Synth';
import { PLAYER } from '../data/balance';
import { getJob } from '../data/jobs';
import { FIRST_MAP, FIRST_SPAWN, MAPS } from '../data/maps';
import { ROSTER, getTitle } from '../data/titles';
import { net } from '../net/NetClient';
import { SaveGame } from '../systems/SaveGame';
import { type PlayerState, type SaveData } from '../systems/PlayerState';
import { AuthOverlay } from '../ui/AuthOverlay';
import { CreateOverlay } from '../ui/CreateOverlay';
import type { AuthCreds } from '../net/protocol';
import { COLOR, TEXT_COLOR, UI_FONT } from '../ui/theme';
import { TOUCH_EVENT, type TouchUi } from '../platform/TouchPad';
import { REG } from './BootScene';

export class TitleScene extends Phaser.Scene {
  private rosterUi?: Phaser.GameObjects.Container;
  private started = false;

  constructor() {
    super('title');
  }

  create(): void {
    const { width, height } = this.scale;
    this.started = false;
    this.drawBackdrop(width, height);

    const title = this.add
      .text(width / 2, 88, '헤네시스 사냥터', {
        fontFamily: UI_FONT,
        fontSize: '48px',
        color: '#ffffff',
        stroke: '#2b4a7a',
        strokeThickness: 10,
        align: 'center',
        wordWrap: { width: width - 80, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.tweens.add({ targets: title, y: 80, duration: 1600, yoyo: true, repeat: -1, ease: 'Sine.InOut' });

    this.add
      .text(width / 2, 132, '2D 횡스크롤 액션 RPG 습작 — 개인 학습용', {
        fontFamily: UI_FONT,
        fontSize: '15px',
        color: '#1f3a5a',
        align: 'center',
        wordWrap: { width: width - 80, useAdvancedWrap: true },
      })
      .setOrigin(0.5);

    this.drawRoster();

    this.add
      .text(
        width / 2,
        height - 48,
        'Enter 시작   N 캐릭터 만들기   1~6 선택   Del 삭제   O 로그인',
        {
          fontFamily: UI_FONT,
          fontSize: '13px',
          color: '#1f3a5a',
          align: 'center',
          wordWrap: { width: width - 48, useAdvancedWrap: true },
        },
      )
      .setOrigin(0.5);

    this.bindKeys();
    this.bindTouch();
  }

  private drawBackdrop(width: number, height: number): void {
    const g = this.add.graphics();
    for (let i = 0; i < 20; i++) {
      const t = i / 19;
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.IntegerToColor(0x6fc0ef),
        Phaser.Display.Color.IntegerToColor(0xdff4ef),
        1,
        t,
      );
      g.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b), 1);
      g.fillRect(0, (height / 20) * i, width, height / 20 + 1);
    }
    g.fillStyle(0x7ed957, 1);
    g.fillRect(0, height - 150, width, 150);
    g.fillStyle(0x49a03c, 1);
    g.fillRect(0, height - 150, width, 10);

    this.add.image(width * 0.18, height - 140, 'decor-mushroom-house').setOrigin(0.5, 1);
    this.add.image(width * 0.84, height - 140, 'decor-tree').setOrigin(0.5, 1);
    this.add.image(width * 0.72, height - 140, 'decor-bush').setOrigin(0.5, 1);
    this.add.sprite(width * 0.46, height - 142, 'mob-snail', 0).setOrigin(0.5, 1);
    this.add.sprite(width * 0.53, height - 142, 'mob-slime', 0).setOrigin(0.5, 1);
    const hero = this.add.sprite(width * 0.36, height - 138, 'player-stand', 0).setOrigin(0.5, 1);
    hero.play('anim-stand');
    for (let i = 0; i < 5; i++) {
      this.add
        .image(Phaser.Math.Between(80, width - 80), Phaser.Math.Between(60, 240), i % 2 ? 'decor-cloud-a' : 'decor-cloud-b')
        .setAlpha(0.9);
    }
  }

  private drawRoster(): void {
    this.rosterUi?.destroy();
    const { width } = this.scale;
    const slots = SaveGame.slots();
    const panelW = 640;
    const panelH = 48 + Math.max(1, slots.length) * 44 + 36;
    const panelY = 168;
    const ui = this.add.container(width / 2, panelY);
    this.rosterUi = ui;

    const g = this.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.78);
    g.fillRoundedRect(-panelW / 2, 0, panelW, panelH, 10);
    ui.add(g);

    ui.add(
      this.add
        .text(0, 12, `캐릭터  ${slots.length}/${ROSTER.maxSlots}`, {
          fontFamily: UI_FONT,
          fontSize: '15px',
          color: TEXT_COLOR.gold,
        })
        .setOrigin(0.5, 0),
    );

    if (slots.length === 0) {
      ui.add(
        this.add
          .text(0, 56, 'N 으로 캐릭터를 만드세요. Enter 는 전사로 바로 시작합니다.', {
            fontFamily: UI_FONT,
            fontSize: '14px',
            color: TEXT_COLOR.bright,
            align: 'center',
            wordWrap: { width: panelW - 40, useAdvancedWrap: true },
          })
          .setOrigin(0.5, 0),
      );
      return;
    }

    slots.forEach((slot, i) => {
      const save = slot.save;
      const selected = slot.id === SaveGame.activeId();
      const y = 48 + i * 44;
      const row = this.add
        .rectangle(0, y + 16, panelW - 24, 40, selected ? 0x2a3a58 : 0x1c1c28, selected ? 0.95 : 0.55)
        .setStrokeStyle(1, selected ? 0xf0d080 : 0x3a3a52)
        .setInteractive({ useHandCursor: true });
      row.on('pointerdown', () => {
        SaveGame.select(slot.id);
        this.drawRoster();
      });
      const title = getTitle(save.titleId);
      const line = `${i + 1}.  ${save.name}   Lv.${save.level} ${getJob(save.jobId).name}   [${title.name}]`;
      const text = this.add
        .text(0, y + 16, line, {
          fontFamily: UI_FONT,
          fontSize: '15px',
          color: TEXT_COLOR.bright,
          wordWrap: { width: panelW - 48, useAdvancedWrap: true },
        })
        .setOrigin(0.5);
      ui.add([row, text]);
    });
  }

  private bindTouch(): void {
    const onUi = (ev: Event): void => {
      const ui = (ev as CustomEvent<TouchUi>).detail;
      const blocked = AuthOverlay.blocking || CreateOverlay.blocking || this.started;
      if (ui === 'start' && !blocked) this.play();
      if (ui === 'create' && !blocked) this.openCreate();
      if (ui === 'login' && !this.started && !AuthOverlay.blocking && !CreateOverlay.blocking) {
        AuthOverlay.show((creds) => this.play(creds));
      }
    };
    window.addEventListener(TOUCH_EVENT, onUi);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener(TOUCH_EVENT, onUi);
    });
  }

  private bindKeys(): void {
    const blocked = (): boolean => AuthOverlay.blocking || CreateOverlay.blocking || this.started;

    this.input.keyboard?.on('keydown-ENTER', () => {
      if (blocked()) return;
      this.play();
    });
    this.input.keyboard?.on('keydown-N', () => {
      if (blocked()) return;
      this.openCreate();
    });
    this.input.keyboard?.on('keydown-C', () => {
      if (blocked()) return;
      this.openCreate();
    });
    this.input.keyboard?.on('keydown-O', () => {
      if (this.started || AuthOverlay.blocking || CreateOverlay.blocking) return;
      AuthOverlay.show((creds) => this.play(creds));
    });
    this.input.keyboard?.on('keydown-DELETE', () => {
      if (blocked()) return;
      const id = SaveGame.activeId();
      if (!id) return;
      SaveGame.remove(id);
      Sfx.uiClick();
      this.drawRoster();
    });
    const nums = ['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX'] as const;
    nums.forEach((key, i) => {
      this.input.keyboard?.on(`keydown-${key}`, () => {
        if (blocked()) return;
        const slot = SaveGame.slots()[i];
        if (!slot) return;
        SaveGame.select(slot.id);
        this.drawRoster();
      });
    });
  }

  private openCreate(): void {
    CreateOverlay.show((name, jobId) => {
      const fail = SaveGame.create(name, jobId);
      if (fail) {
        CreateOverlay.error(fail);
        return;
      }
      CreateOverlay.hide();
      Sfx.uiClick();
      this.drawRoster();
    });
  }

  private play(auth?: AuthCreds): void {
    if (this.started) return;
    this.started = true;
    synth.start();
    Sfx.uiClick();
    const state = this.registry.get(REG.state) as PlayerState;

    void (async () => {
      if (!SaveGame.exists()) {
        const fail = SaveGame.create(PLAYER.defaultName, 'warrior');
        if (fail) {
          this.started = false;
          this.openCreate();
          CreateOverlay.error(fail);
          return;
        }
      }

      const save = SaveGame.cached();
      if (!save) {
        this.started = false;
        return;
      }
      state.load(save);

      let mapId = MAPS[save.mapId] ? save.mapId : FIRST_MAP;
      let spawnId = save.spawnId || FIRST_SPAWN;

      if (auth) {
        AuthOverlay.error(auth.mode === 'register' ? '가입 요청 중…' : '로그인 요청 중…');
        const fail = await net.connect(auth, state.jobId, state.toJSON());
        if (fail) {
          this.started = false;
          AuthOverlay.error(fail);
          return;
        }
        AuthOverlay.hide();
        const remote = net.welcomeSave as SaveData | null;
        if (remote && typeof remote.version === 'number' && remote.mapId) {
          state.load(remote);
          mapId = MAPS[remote.mapId] ? remote.mapId : FIRST_MAP;
          spawnId = remote.spawnId || FIRST_SPAWN;
        }
      }
      bgm.play(MAPS[mapId].bgmMood);
      this.scene.start('play', { mapId, spawnId });
      this.scene.launch('hud');
    })();
  }
}
